import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import { useToast } from 'primevue/usetoast';

import { useUpdateBeneficiaryStore, type useBeneficiariesStore } from '@peanuds/shared-state';
import {
  createBankingCircleTextSchema,
  createIbanSchema,
  isMfaRequiredSentinelError,
  isPendingLivenessCheckSentinelError,
  normalizeIban,
} from '@peanuds/shared';
import type { UpdateBeneficiaryDetailsRequestDto } from '@peanuds/shared';

import { BENEFICIARY_NOT_ACTIVE_CODE } from '../internal/contact-errors';
import type {
  BeneficiaryBankAccount,
  BeneficiaryBankAccountHolderType,
  Contact,
} from '../types/contact-information-dialog.types';

type BeneficiariesStore = ReturnType<typeof useBeneficiariesStore>;

type UseUpdateContactDetailsOptions = {
  activeContact: ComputedRef<Contact | null>;
  activeContactId: ComputedRef<string | undefined>;
  bankAccounts: Ref<BeneficiaryBankAccount[]>;
  beneficiariesStore: BeneficiariesStore;
  isEditMode: Ref<boolean>;
  selectedBeneficiaryAccountHolderType: ComputedRef<BeneficiaryBankAccountHolderType | null>;
};

const IBAN_ERROR_MESSAGE_KEY = 'beneficiary_bank_form.validation.iban';
const DUPLICATE_IBAN_MESSAGE_KEY = 'contacts.dialog.bank_account_exists';
const FULL_NAME_REQUIRED_MESSAGE_KEY = 'contacts.dialog.contact_name_full_required';
const UPDATE_FAILED_MESSAGE_KEY = 'contacts.dialog.update_failed';
const UPDATE_LOCKED_ERROR_CODE = 'BENEFICIARY_BANK_ACCOUNT.UPDATE_LOCKED';

const ibanSchema = createIbanSchema({ errorMessage: IBAN_ERROR_MESSAGE_KEY });
const nameSchema = createBankingCircleTextSchema({ required: true });

const toIndividualNameParts = (
  fullName: string,
): { firstName: string; lastName: string } | null => {
  const [firstName, ...familyNameParts] = fullName.split(/\s+/).filter((part) => part.length > 0);
  const lastName = familyNameParts.join(' ');

  if (!firstName || !lastName) {
    return null;
  }

  return { firstName, lastName };
};

export const useUpdateContactDetails = ({
  activeContact,
  activeContactId,
  bankAccounts,
  beneficiariesStore,
  isEditMode,
  selectedBeneficiaryAccountHolderType,
}: UseUpdateContactDetailsOptions) => {
  const { t } = useTranslation();
  const toast = useToast();
  const updateBeneficiaryStore = useUpdateBeneficiaryStore();

  const editedName = ref('');
  const isNameTouched = ref(false);
  const saveFeedbackMessageKey = ref<string | null>(null);
  // Edited IBAN values keyed by the normalized original IBAN of the bank account.
  const ibanEdits = ref<Record<string, string>>({});

  const contactName = computed(() => activeContact.value?.name ?? '');

  watch(isEditMode, (isEditing) => {
    if (!isEditing) {
      return;
    }

    editedName.value = contactName.value;
    isNameTouched.value = false;
    saveFeedbackMessageKey.value = null;
    ibanEdits.value = {};
    updateBeneficiaryStore.resetUpdateState();
  });

  const resolveEditedIban = (account: BeneficiaryBankAccount): string | null => {
    if (!account.iban) {
      return null;
    }

    return ibanEdits.value[normalizeIban(account.iban)] ?? account.iban;
  };

  const getNameErrorMessageKey = (): string | null => {
    const parseResult = nameSchema.safeParse(editedName.value);
    if (!parseResult.success) {
      return parseResult.error.issues[0]?.message ?? null;
    }

    const requiresSplitName =
      selectedBeneficiaryAccountHolderType.value === 'individual' &&
      toIndividualNameParts(parseResult.data) === null;

    return requiresSplitName ? FULL_NAME_REQUIRED_MESSAGE_KEY : null;
  };

  const nameErrorMessageKey = computed(() =>
    isNameTouched.value ? getNameErrorMessageKey() : null,
  );

  const ibanErrorMessageKeys = computed<(string | null)[]>(() => {
    const editedIbans = bankAccounts.value.map((account) => {
      const editedIban = resolveEditedIban(account);
      return editedIban === null ? null : normalizeIban(editedIban);
    });

    return editedIbans.map((iban, index) => {
      if (iban === null) {
        return null;
      }

      const parseResult = ibanSchema.safeParse(iban);
      if (!parseResult.success) {
        return parseResult.error.issues[0]?.message ?? IBAN_ERROR_MESSAGE_KEY;
      }

      const isDuplicate = editedIbans.some(
        (otherIban, otherIndex) => otherIndex !== index && otherIban === iban,
      );

      return isDuplicate ? DUPLICATE_IBAN_MESSAGE_KEY : null;
    });
  });

  const hasValidationErrors = computed(
    () =>
      getNameErrorMessageKey() !== null ||
      ibanErrorMessageKeys.value.some((messageKey) => messageKey !== null),
  );

  const isUpdatingContactDetails = computed(() => updateBeneficiaryStore.isUpdating);

  const updateFeedbackMessage = computed(() => {
    if (saveFeedbackMessageKey.value !== null) {
      return t(saveFeedbackMessageKey.value);
    }

    const error = updateBeneficiaryStore.updateError;
    if (error === undefined) {
      return null;
    }

    if (isPendingLivenessCheckSentinelError(error) || isMfaRequiredSentinelError(error)) {
      return null;
    }

    if (updateBeneficiaryStore.updateErrorCode === UPDATE_LOCKED_ERROR_CODE) {
      return t('contacts.dialog.update_locked');
    }

    if (updateBeneficiaryStore.updateErrorCode === BENEFICIARY_NOT_ACTIVE_CODE) {
      return t('contacts.dialog.not_active');
    }

    return updateBeneficiaryStore.updateErrorMessage ?? t(UPDATE_FAILED_MESSAGE_KEY);
  });

  const markNameTouched = () => {
    isNameTouched.value = true;
  };

  const handleIbanChange = (index: number, nextIban: string) => {
    const account = bankAccounts.value[index];
    if (!account?.iban) {
      return;
    }

    ibanEdits.value = {
      ...ibanEdits.value,
      [normalizeIban(account.iban)]: nextIban,
    };
  };

  const toUpdateIdentity = (
    fullName: string,
  ): UpdateBeneficiaryDetailsRequestDto['identity'] | null => {
    const holderType = selectedBeneficiaryAccountHolderType.value;

    if (holderType === 'corporate') {
      return { type: 'corporate', legalName: fullName };
    }

    if (holderType === 'individual') {
      const nameParts = toIndividualNameParts(fullName);
      return nameParts === null ? null : { type: 'individual', ...nameParts };
    }

    return null;
  };

  const buildBankAccountChanges = () =>
    bankAccounts.value
      .filter((account): account is BeneficiaryBankAccount & { id: string; iban: string } =>
        Boolean(account.id && account.iban),
      )
      .map((account) => ({
        bankAccountId: account.id,
        nextIban: normalizeIban(resolveEditedIban(account) ?? account.iban),
        previousIban: normalizeIban(account.iban),
      }))
      .filter(({ previousIban, nextIban }) => previousIban !== nextIban);

  const refreshBeneficiariesIfRemoved = async (): Promise<void> => {
    if (updateBeneficiaryStore.updateErrorCode === BENEFICIARY_NOT_ACTIVE_CODE) {
      await beneficiariesStore.fetchBeneficiaries();
    }
  };

  const saveContactDetails = async (): Promise<boolean> => {
    const beneficiaryId = activeContactId.value;
    if (!beneficiaryId || isUpdatingContactDetails.value) {
      return false;
    }

    isNameTouched.value = true;
    saveFeedbackMessageKey.value = null;
    if (hasValidationErrors.value) {
      return false;
    }

    const trimmedName = editedName.value.trim();
    const fullName = trimmedName !== contactName.value.trim() ? trimmedName : undefined;
    const bankAccountChanges = buildBankAccountChanges();

    if (fullName === undefined && bankAccountChanges.length === 0) {
      return true;
    }

    if (fullName !== undefined) {
      const identity = toUpdateIdentity(fullName);
      if (identity === null) {
        saveFeedbackMessageKey.value = UPDATE_FAILED_MESSAGE_KEY;
        return false;
      }

      const isUpdated = await updateBeneficiaryStore.updateBeneficiaryDetails({
        path: { id: beneficiaryId },
        body: { identity },
      });
      if (!isUpdated) {
        await refreshBeneficiariesIfRemoved();
        return false;
      }
    }

    for (const change of bankAccountChanges) {
      const isUpdated = await updateBeneficiaryStore.updateBeneficiaryBankAccount({
        path: { id: beneficiaryId, bankAccountId: change.bankAccountId },
        body: { iban: change.nextIban },
      });
      if (!isUpdated) {
        await refreshBeneficiariesIfRemoved();
        return false;
      }
    }

    toast.add({
      severity: 'success',
      summary: t('contacts.dialog.update_success_summary'),
      detail: t('contacts.dialog.update_success_detail'),
      life: 5000,
    });
    updateBeneficiaryStore.resetUpdateState();
    await beneficiariesStore.fetchBeneficiaries();

    return true;
  };

  return {
    editedName,
    handleIbanChange,
    ibanErrorMessageKeys,
    isUpdatingContactDetails,
    markNameTouched,
    nameErrorMessageKey,
    saveContactDetails,
    updateFeedbackMessage,
  };
};
